// Vue
import { Component, Prop, Provide, Vue, Watch } from "vue-property-decorator";
// Template
import template from "./template.vue";
import { Header } from "../header/Header";
import { HttpService } from "../../../services/HttpService";
import { IRegion } from "../../../interfaces/IRegion";
import { ILanguage } from "../../../interfaces/ILanguage";
import { Language } from "../../../enums/Language";
import { IUserinfo } from "../../../interfaces/IUserinfo";
import { IUserinfoError } from "../../../interfaces/IUserinfoError";
import { User } from "../../../models/user/User";
import { PrivacyPolicy } from "../privacy_policy/PrivacyPolicy";
import { ButtonLink } from "../button_link/ButtonLink";
import { HeaderFavorites } from "../header_favorites/HeaderFavorites";
import { HeaderVehicleConfigs } from "../header_vehicle_configs/HeaderVehicleConfigs";

@Component({
  mixins: [template],
  components: {
    Header,
    PrivacyPolicy,
    ButtonLink,
    HeaderFavorites,
    HeaderVehicleConfigs,
  },
})
export class HeaderDefault extends Vue {
  private readonly http: HttpService = HttpService.instance;

  public headerRegions: IRegion[] = [];

  private userInfo: IUserinfo | IUserinfoError | null = null;

  private isPrivacyPolicyVisible: boolean = false;

  private privacyPolicyActiveTab: string = "";

  @Prop({ default: "" })
  private activeFlyout!: string;

  @Prop({ default: null })
  public privacyPolicyBaseUrl!: string | null;

  @Prop({ default: null })
  public anbieterUrl!: string | null;

  @Prop({ default: null })
  public rechtlicheHinweiseUrl!: string | null;

  @Prop({ default: null })
  public cookiesUrl!: string | null;

  @Prop({ default: null })
  public datenschutzUrl!: string | null;

  @Prop({ default: null })
  public lizenzhinweiseDritterUrl!: string | null;

  @Prop()
  public language!: string;

  @Prop()
  public country!: string;

  @Prop({
    default: () => {
      return window.location.origin;
    },
  })
  public redirectUrlAfterLogout!: string;

  @Prop({
    default: () => {
      return window.location.origin;
    },
  })
  public homeLink!: string;

  @Prop({
    default: () => {
      return [];
    },
  })
  public supportedLanguages!: ILanguage[];

  @Prop({ default: false })
  public willProvideRegions!: boolean;

  @Prop({
    default: () => {
      return [];
    },
  })
  public supportedRegions!: IRegion[];

  @Prop({ default: false })
  public disableAuth!: boolean;

  @Prop({ default: null })
  public options!: object | null;

  @Prop({ default: false })
  public enableFavorites!: boolean;

  @Prop({ default: false })
  public showHeaderManually!: boolean;

  @Prop({ default: false })
  public showMobileMenuManually!: boolean;

  @Prop({ default: false })
  public isMobileMenuOpen!: boolean;

  @Prop({ default: false })
  public enableVehicleConfigs!: boolean;

  @Prop({ default: "" })
  public activeOverlay!: string;

  private allLanguages: ILanguage[] = [
    { id: 1, code: Language.BR, name: "Português brasileiro" },
    { id: 2, code: Language.CS, name: "Česky" },
    { id: 3, code: Language.DE, name: "Deutsch" },
    { id: 4, code: Language.EN, name: "English" },
    { id: 5, code: Language.ES, name: "Español" },
    { id: 6, code: Language.FR, name: "Français" },
    { id: 7, code: Language.IT, name: "Italiano" },
    { id: 8, code: Language.NL, name: "Nederlands" },
    { id: 9, code: Language.PL, name: "Polski" },
    { id: 10, code: Language.PT, name: "Português" },
    { id: 11, code: Language.RU, name: "Pусский" },
    { id: 12, code: Language.SV, name: "Svenska" },
    { id: 13, code: Language.TR, name: "Türkçe" },
    { id: 14, code: Language.ZH, name: "中文" },
    { id: 15, code: Language.EL, name: "ελληνικά" },
    { id: 16, code: Language.RO, name: "Română" },
    { id: 17, code: Language.SK, name: "Slovenčina" },
    { id: 18, code: Language.KO, name: "한국의" },
  ];

  public async mounted() {
    if (!this.willProvideRegions) {
      this.headerRegions = await this.getRegions();
    }
    if (!this.disableAuth) {
      if (process.env.NODE_ENV === "local" || process.env.NODE_ENV === "development") {
        this.userInfo = await this.getUserInfo("", "");
      } else {
        this.userInfo = await this.getUserInfo(this.getRedirectUrlAfterLogin(), this.redirectUrlAfterLogout);
      }
      if (this.userInfo) {
        if ((this.userInfo as IUserinfo).tracking_enabled !== undefined) {
          if ((this.userInfo as IUserinfo).tracking_enabled) {
            if (window["_paq"]) {
              (window as any)._paq.push(["forgetUserOptOut"]);
            }
          } else {
            if (window["_paq"]) {
              (window as any)._paq.push(["optUserOut"]);
            }
          }
        }
        (<IUserinfo>this.userInfo).tracking_enabled;
        this.$emit("user-logged-in", (<IUserinfo>this.userInfo).hasOwnProperty("logout_url"));
      }
    }
  }

  private isLocalizedUrl(path) {
    return (
      path.match(/^\/[a-z]{2}\/([A-Z]{2}|GLOBAL|--)\/?$/) || path.match(/^\/[a-z]{2}\/([A-Z]{2}|GLOBAL|--)\/(.+)$/)
    );
  }

  private getRedirectUrlAfterLogin() {
    let path = window.location.pathname;
    if (this.isLocalizedUrl(path)) {
      let parts = path.split("/");
      parts[1] = "--";
      parts[2] = "--";
      let newPath = parts.join("/");

      return window.location.href.replace(path, newPath);
    }

    return window.location.href;
  }

  @Watch("supportedRegions")
  public onWatchSupportedRegions() {
    if (this.willProvideRegions) {
      this.headerRegions = this.supportedRegions;
    }
  }

  get finalPrivacyPolicyBaseUrl() {
    if (this.privacyPolicyBaseUrl) {
      return this.privacyPolicyBaseUrl;
    } else {
      return `${window.location.origin}/${this.language}`;
    }
  }

  get headerLanguages() {
    if (this.supportedLanguages.length <= 0) {
      return this.allLanguages;
    } else {
      return this.supportedLanguages;
    }
  }

  get headerAuthOptions() {
    if (this.disableAuth) {
      return null;
    } else {
      if (this.userInfo && (<IUserinfo>this.userInfo).logout_url) {
        let info = <IUserinfo>this.userInfo;
        return new User({
          is_logged_in: true,
          name: info.displayName,
          logout_url: info.logout_url,
          has_multi_user: info.has_multi_user,
          multi_user_url: info.multi_user_url,
        });
      } else if (this.userInfo && (<IUserinfoError>this.userInfo).login_url) {
        let info = <IUserinfoError>this.userInfo;
        return new User({
          is_logged_in: false,
          login_url: info.login_url,
        });
      } else {
        return new User({
          is_logged_in: false,
          login_url: "",
        });
      }
    }
  }

  get defaultHeaderOptions(): object {
    return {
      topMenuBefore: [],
      menu: [
        {
          id: "dashboard",
          href: this.homeLink,
          icon: this.$icons.DASHBOARD,
          text: (this as any).$t("HEADER_DEFAULT_DASHBOARD"),
        },
      ],
    };
  }

  get finalHeaderOptions() {
    let options = this.defaultHeaderOptions;
    if (this.options) {
      options = this.options;
    }

    if (this.isLoggedIn && this.enableFavorites) {
      if (!options["menu"]) {
        options["menu"] = [];
      }

      options["menu"].unshift({
        id: "favorites",
        overlay: true,
        icon: this.$icons.RECOMMENDED,
        text: (this as any).$t("HEADER_DEFAULT_FAVORITES"),
      });
    }

    if (this.isLoggedIn && this.enableVehicleConfigs) {
      if (!options["menu"]) {
        options["menu"] = [];
      }

      options["menu"].unshift({
        id: "vehicle-configs",
        overlay: true,
        icon: this.$icons.SETTINGS,
        text: (this as any).$t("HEADER_DEFAULT_VEHICLE_CONFIGS"),
      });
    }

    return options;
  }

  get isLoggedIn() {
    return true || (this.userInfo && (<IUserinfo>this.userInfo).logout_url);
  }

  get isUebernameActive() {
    return this.userInfo && (<IUserinfo>this.userInfo).uebername;
  }

  public changeCountry(event: any) {
    if (this.isLocalizedUrl(window.location.pathname)) {
      let pathArray = window.location.pathname.split("/");
      pathArray[2] = event.krzl.toUpperCase();

      window.location.href = pathArray.join("/") + window.location.search;
    } else {
      var searchParams = new URLSearchParams(window.location.search);
      searchParams.set("R", event.krzl.toUpperCase());

      window.location.search = searchParams.toString();
    }
    this.$emit("change-country", event);
  }

  public changeLanguage(event: any) {
    if (this.isLocalizedUrl(window.location.pathname)) {
      let pathArray = window.location.pathname.split("/");
      pathArray[1] = event.code;

      window.location.href = pathArray.join("/") + window.location.search;
    } else {
      var searchParams = new URLSearchParams(window.location.search);
      searchParams.set("L", event.code);

      window.location.search = searchParams.toString();
    }
    this.$emit("change-language", event);
  }

  public async getRegions() {
    let url = `${APP_API_URL}cms/${this.language}/${this.country}/regionen`;
    let response = await this.http.get<IRegion[]>(url);

    return response.body!;
  }

  public async getUserInfo(redirectUrlAfterLogin: string, redirectUrlAfterLogout: string) {
    try {
      let url = `${APP_API_URL}auth/v2.0/${this.language}/userinfo`;
      const response = await this.http.get<IUserinfo>(url, {
        params: {
          redirect_url_after_login: redirectUrlAfterLogin,
          redirect_url_after_logout: redirectUrlAfterLogout,
        },
      });

      return response.body!;
    } catch (e: any) {
      if (e.status == 401) {
        return e.body!;
      } else {
        throw e;
      }
    }
  }

  public handleHeaderTopItemClick(itemName: string) {
    switch (itemName) {
      case "privacy-policy":
        this.privacyPolicyActiveTab = "anbieter";
        this.isPrivacyPolicyVisible = !this.isPrivacyPolicyVisible;
        break;
    }
  }

  public openPolicy(itemName: string) {
    this.privacyPolicyActiveTab = itemName;
    this.isPrivacyPolicyVisible = true;
  }

  public closePolicy() {
    this.isPrivacyPolicyVisible = false;
  }

  public handleBodyPaddingChanged(event) {
    this.$emit("body-padding-changed", event);
  }

  public handleFlyoutUpdate(event) {
    this.$emit("update:activeFlyout", event);
  }

  public handleIsMobileMenuOpen(event) {
    this.$emit("update:isMobileMenuOpen", event);
  }
}
